import {
  FloatingFocusManager,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react"
import classNames from "classnames"
import { IconButton } from "../IconButton"
import { ModalProps } from "./types"
import { Overlay } from "../Overlay"
import { ModalHeader } from "./ModalHeader"
import { ModalContent } from "./ModalContent"
import { ModalFooter } from "./ModalFooter"

/**
 * The Modal component exports the following components to help you create a Modal dialog
 * - `Modal`
 * - `Modal.Header`
 * - `Modal.Content`
 * - `Modal.Footer`
 *
 * A basic Modal with title and footer should look like this
 *
 * ```tsx
 * <Modal>
 *  <Modal.Header>My modal title</Modal.Header>
 *  <Modal.Content>
 *    My modal content. Lorem ipsum dolor sit amet consectetur
 *    adipisicing elit. Vero deleniti at architecto illum ex.
 *  </Modal.Content>
 *  <Modal.Footer>
 *    <Button className="md:w-fit" variation="outline">
        Cancel
      </Button>
      <Button className="md:w-fit">
        Proceed
      </Button>
 *  </Modal.Footer>
 * </Modal>
 * ```
 */

export function Modal({
  children,
  closeButtonLabel = "Close dialog",
  isOpen = true,
  onRequestClose,
  rootElement,
  rootId,
  isCloseable = true,
  size = "md",
  ...rest
}: ModalProps) {
  const isSM = size === "sm"
  const isMD = size === "md"
  const isLG = size === "lg"

  const {
    refs: { setFloating },
    context,
  } = useFloating({
    open: isOpen,
    onOpenChange: open => {
      if (!open && isCloseable) {
        onRequestClose()
      }
    },
  })
  const { getFloatingProps } = useInteractions([
    useRole(context, { role: "dialog" }),
    useDismiss(context, {
      escapeKey: true,
      enabled: isCloseable,
    }),
  ])

  if (!isOpen) {
    return null
  }

  return (
    <FloatingPortal id={rootId} root={rootElement}>
      <Overlay lockScroll={true} className="grid grid-cols-[16px_auto_16px] place-items-center">
        <FloatingFocusManager context={context} modal>
          <div
            aria-modal="true"
            className={classNames(
              "relative col-start-2 flex max-h-[calc(100vh_-_32px)] w-full flex-col gap-4 rounded-md bg-white pb-4  pt-6 md:max-h-[calc(100vh_-_64px)]",
              {
                "max-w-[400px]": isSM,
                "max-w-[400px] sm:max-w-[520px]": isMD,
                "max-w-[400px] sm:max-w-[640px] lg:max-w-[800px]": isLG,
              },
            )}
            ref={setFloating}
            {...rest}
            {...getFloatingProps()}
          >
            {isCloseable && (
              <IconButton
                aria-label={closeButtonLabel}
                className="absolute right-3 top-3 z-50" // Mimic p-5 to the edge: button provides padding 2 already, p-5 (total) - p-2 (provided by button) = 3.
                onClick={onRequestClose}
                iconName="Times"
                size="sm"
                variation="borderless"
              />
            )}
            {children}
          </div>
        </FloatingFocusManager>
      </Overlay>
    </FloatingPortal>
  )
}

Modal.Header = ModalHeader
Modal.Content = ModalContent
Modal.Footer = ModalFooter
